import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Plus, Handshake, ExternalLink, Pencil, Trash2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalBadge } from "@/components/ui/BrutalBadge";
import {
  BrutalSelect,
  BrutalSelectContent,
  BrutalSelectItem,
  BrutalSelectTrigger,
  BrutalSelectValue,
} from "@/components/ui/BrutalSelect";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import KPICard from "@/components/admin/KPICard";
import { PartnershipFormModal } from "@/components/admin/PartnershipFormModal";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Partnership {
  id: string;
  name: string;
  image_url: string | null;
  description_it: string | null;
  description_en: string | null;
  description_es: string | null;
  discount_code: string | null;
  redemption_url: string | null;
  category: string | null;
  is_active: boolean;
  display_order: number;
  created_at: string;
}

interface ClickStats {
  partnership_id: string;
  total_clicks: number;
  unique_clicks: number;
}

const CATEGORY_KEYS = ["all", "software", "formazione", "servizi", "prodotti", "altro"];

export default function AdminPartnerships() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [partnerships, setPartnerships] = useState<Partnership[]>([]);
  const [clickStats, setClickStats] = useState<Record<string, ClickStats>>({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPartnership, setEditingPartnership] = useState<Partnership | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const fetchPartnerships = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("partnerships")
      .select("*")
      .order("display_order", { ascending: true });

    if (error) {
      toast({
        title: t("common.error"),
        description: t("admin.partnerships.loadError"),
        variant: "error",
      });
    } else {
      setPartnerships(data || []);
    }
    setLoading(false);
  };

  const fetchClickStats = async () => {
    const { data, error } = await supabase.rpc('get_partnership_click_stats');
    if (!error && data) {
      const statsMap: Record<string, ClickStats> = {};
      (data as ClickStats[]).forEach((stat) => {
        statsMap[stat.partnership_id] = stat;
      });
      setClickStats(statsMap);
    }
  };

  useEffect(() => {
    fetchPartnerships();
    fetchClickStats();
  }, []);

  const filteredPartnerships = partnerships.filter((p) => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || p.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleEdit = (partnership: Partnership) => {
    setEditingPartnership(partnership);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("partnerships").delete().eq("id", id);
    if (error) {
      toast({
        title: t("common.error"),
        description: t("admin.partnerships.deleteError"),
        variant: "error",
      });
    } else {
      toast({ title: t("admin.partnerships.deleted") });
      fetchPartnerships();
    }
    setDeleteId(null);
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    const { error } = await supabase
      .from("partnerships")
      .update({ is_active: !isActive })
      .eq("id", id);
    if (error) {
      toast({
        title: t("common.error"),
        description: t("admin.partnerships.updateError"),
        variant: "error",
      });
    } else {
      fetchPartnerships();
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingPartnership(null);
  };

  const handleSuccess = () => {
    handleModalClose();
    fetchPartnerships();
    fetchClickStats();
  };

  const activeCount = partnerships.filter((p) => p.is_active).length;
  const withDiscountCount = partnerships.filter((p) => p.discount_code).length;
  const totalClicks = Object.values(clickStats).reduce((sum, s) => sum + s.total_clicks, 0);

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/dashboard">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("admin.partnerships.title")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/admin/dashboard")}
              className="p-2 hover:bg-accent transition-colors rounded-lg"
              aria-label={t("common.back")}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
                <Handshake className="w-10 h-10 text-primary" />
                {t("admin.partnerships.title")}
              </h1>
              <p className="text-muted-foreground mt-1">
                {t("admin.partnerships.subtitle")}
              </p>
            </div>
          </div>
          <BrutalButton onClick={() => setIsModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t("admin.partnerships.create")}
          </BrutalButton>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KPICard
            title={t("admin.partnerships.total")}
            value={partnerships.length}
          />
          <KPICard
            title={t("admin.partnerships.active")}
            value={activeCount}
          />
          <KPICard
            title={t("admin.partnerships.withDiscount")}
            value={withDiscountCount}
          />
          <KPICard
            title={t("admin.partnerships.totalClicks")}
            value={totalClicks}
          />
        </div>

        {/* Filters */}
        <BrutalCard className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <BrutalInput
                placeholder={t("admin.partnerships.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="w-full md:w-48">
              <BrutalSelect value={categoryFilter} onValueChange={setCategoryFilter}>
                <BrutalSelectTrigger>
                  <BrutalSelectValue />
                </BrutalSelectTrigger>
                <BrutalSelectContent>
                  {CATEGORY_KEYS.map((key) => (
                    <BrutalSelectItem key={key} value={key}>
                      {t(`admin.partnerships.categories.${key}`)}
                    </BrutalSelectItem>
                  ))}
                </BrutalSelectContent>
              </BrutalSelect>
            </div>
          </div>
        </BrutalCard>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t("common.loading")}</p>
          </div>
        ) : filteredPartnerships.length === 0 ? (
          <BrutalCard className="p-12 text-center">
            <Handshake className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {t("admin.partnerships.empty")}
            </p>
          </BrutalCard>
        ) : (
          <BrutalCard>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface border-b-[3px] border-dark">
                  <tr>
                    <th className="px-4 py-3 text-left font-heading font-bold">{t("admin.partnerships.table.name")}</th>
                    <th className="px-4 py-3 text-left font-heading font-bold">{t("admin.partnerships.table.category")}</th>
                    <th className="px-4 py-3 text-left font-heading font-bold">{t("admin.partnerships.table.discountCode")}</th>
                    <th className="px-4 py-3 text-left font-heading font-bold">{t("admin.partnerships.table.redemptionLink")}</th>
                    <th className="px-4 py-3 text-center font-heading font-bold">{t("admin.partnerships.table.totalClicks")}</th>
                    <th className="px-4 py-3 text-center font-heading font-bold">{t("admin.partnerships.table.uniqueClicks")}</th>
                    <th className="px-4 py-3 text-center font-heading font-bold">{t("admin.partnerships.table.average")}</th>
                    <th className="px-4 py-3 text-right font-heading font-bold">{t("admin.partnerships.table.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPartnerships.map((partnership) => (
                    <tr key={partnership.id} className="border-b border-border hover:bg-surface/50 transition-colors">
                      {/* Nome con immagine */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 border-2 border-dark overflow-hidden bg-muted flex-shrink-0">
                            {partnership.image_url ? (
                              <img
                                src={partnership.image_url}
                                alt={partnership.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Handshake className="h-4 w-4 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-medium truncate">{partnership.name}</span>
                            <BrutalBadge variant={partnership.is_active ? "default" : "secondary"}>
                              {partnership.is_active ? t("admin.partnerships.statusActive") : t("admin.partnerships.statusInactive")}
                            </BrutalBadge>
                          </div>
                        </div>
                      </td>

                      {/* Categoria */}
                      <td className="px-4 py-3">
                        {partnership.category ? (
                          <BrutalBadge variant="secondary">
                            {t(`admin.partnerships.categories.${partnership.category}`)}
                          </BrutalBadge>
                        ) : (
                          "-"
                        )}
                      </td>

                      {/* Codice */}
                      <td className="px-4 py-3">
                        {partnership.discount_code ? (
                          <code className="bg-muted px-2 py-1 rounded font-mono text-sm border border-border">
                            {partnership.discount_code}
                          </code>
                        ) : (
                          "-"
                        )}
                      </td>

                      {/* Link */}
                      <td className="px-4 py-3">
                        {partnership.redemption_url ? (
                          <a
                            href={partnership.redemption_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-primary hover:underline font-medium"
                          >
                            {t("admin.partnerships.table.redemptionLink")}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>

                      {/* Click Totali */}
                      <td className="px-4 py-3 text-center font-mono text-sm">
                        {clickStats[partnership.id]?.total_clicks || 0}
                      </td>

                      {/* Click Unici */}
                      <td className="px-4 py-3 text-center font-mono text-sm">
                        {clickStats[partnership.id]?.unique_clicks || 0}
                      </td>

                      {/* Media */}
                      <td className="px-4 py-3 text-center font-mono text-sm">
                        {(() => {
                          const stats = clickStats[partnership.id];
                          if (!stats || stats.unique_clicks === 0) return "-";
                          return (stats.total_clicks / stats.unique_clicks).toFixed(1);
                        })()}
                      </td>

                      {/* Azioni */}
                      <td className="px-4 py-3 text-right">
                        <TooltipProvider>
                          <div className="flex items-center justify-end gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <BrutalButton
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleToggleActive(partnership.id, partnership.is_active)}
                                >
                                  {partnership.is_active ? (
                                    <EyeOff className="h-4 w-4" />
                                  ) : (
                                    <Eye className="h-4 w-4" />
                                  )}
                                </BrutalButton>
                              </TooltipTrigger>
                              <TooltipContent>
                                {partnership.is_active ? t("admin.partnerships.deactivate") : t("admin.partnerships.activate")}
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <BrutalButton
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEdit(partnership)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </BrutalButton>
                              </TooltipTrigger>
                              <TooltipContent>{t("admin.partnerships.edit")}</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <BrutalButton
                                  variant="danger"
                                  size="sm"
                                  onClick={() => setDeleteId(partnership.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </BrutalButton>
                              </TooltipTrigger>
                              <TooltipContent>{t("admin.partnerships.delete")}</TooltipContent>
                            </Tooltip>
                          </div>
                        </TooltipProvider>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </BrutalCard>
        )}

        {/* Modal */}
        <PartnershipFormModal
          open={isModalOpen}
          onClose={handleModalClose}
          onSuccess={handleSuccess}
          partnership={editingPartnership}
        />

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("admin.partnerships.deleteConfirmTitle")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("admin.partnerships.deleteConfirmDescription")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteId && handleDelete(deleteId)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t("admin.partnerships.delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
